// src/pages/UserProfile.js
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeCanvas } from 'qrcode.react';
import { fetchWithAuth } from '../utils/fetchWithAuth';

const UserProfile = () => {
  const [user, setUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [showNetwork] = useState(true);
  const [showQR, setShowQR] = useState(false);

  const [networkData, setNetworkData] = useState([]);
  const [nodeColors, setNodeColors] = useState({});
  const [selectedNodeId] = useState(null);
  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);

  // Cashback states
  const [cbBase, setCbBase] = useState(0);
  const [cbL1, setCbL1] = useState(0);
  const [cbL2, setCbL2] = useState(0);
  const [cbL3, setCbL3] = useState(0);
  const [cbTotal, setCbTotal] = useState(0);

  // Per-node cashback
  const [cbByNode, setCbByNode] = useState({});

  const [walletBalance, setWalletBalance] = useState(0);
  const [walletLoading, setWalletLoading] = useState(false);

  const [receipts, setReceipts] = useState([]);

  const [refBonusByChild, setRefBonusByChild] = useState({});
  const [refBonusTotal, setRefBonusTotal] = useState(0);

  const { t, i18n } = useTranslation();

  const fieldStyle = { display: 'flex', alignItems: 'center', marginBottom: 10 };
  const labelStyle = { width: 120, fontWeight: 'bold' };
  const inputStyle = { width: 200, padding: 8, boxSizing: 'border-box' };

  const levelGap = 80;
  const radius = 16;
  const spacingX = 60;
  const offset = radius + 20;

  const faceByColor = { red: 'sad', gray: 'awkward', green: 'smile' };

  const ymNow = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  };

const parseReferralSourceChildId = (sourceId) => {
  if (!sourceId) return null
  if (typeof sourceId === 'number') return sourceId

  const s = String(sourceId)
  const id = Number(s.split(':')[0])
  return Number.isFinite(id) ? id : null
}

  const formatCurrency = (n) => {
    const v = Number(n || 0);
    return `${v.toFixed(2)} €`;
  };

  // === INIT ===
  useEffect(() => {
    const lang = localStorage.getItem('lang') || 'sk';
    i18n.changeLanguage(lang);
    (async () => {
      const res = await fetchWithAuth('/auth/me', {}, lang);
      if (!res.ok) return;
      const data = await res.json();
      setUser(data);
      setForm(data);
    })();
  }, [i18n]);

  useEffect(() => {
    if (!showNetwork || !user) return;
    (async () => {
      const res = await fetchWithAuth(`/referral/network/${user.id}`);
      if (!res.ok) return;
      setNetworkData(await res.json());
    })();
  }, [showNetwork, user]);

  // === Cashback sumár ===
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const month = ymNow();
        const res = await fetchWithAuth(`/cashback/${user.id}?month=${encodeURIComponent(month)}`);
        if (res.ok) {
          const row = await res.json();
          setCbBase(Number(row?.base) || 0);
          setCbL1(Number(row?.l1) || 0);
          setCbL2(Number(row?.l2) || 0);
          setCbL3(Number(row?.l3) || 0);
          setCbTotal(Number(row?.total) || 0);
        } else {
          setCbBase(0); setCbL1(0); setCbL2(0); setCbL3(0); setCbTotal(0);
        }
      } catch {
        setCbBase(0); setCbL1(0); setCbL2(0); setCbL3(0); setCbTotal(0);
      }
    })();
  }, [user]);

  // === Cashback per-node ===
  useEffect(() => {
    if (!showNetwork || !user) return;
    (async () => {
      try {
        const month = ymNow();
        const res = await fetchWithAuth(`/cashback/${user.id}/by-node?month=${encodeURIComponent(month)}`);
        if (res.ok) {
          const payload = await res.json();
          const map = {};
          (payload.items || []).forEach(it => {
            map[Number(it.child_id)] = { amount: Number(it.amount) || 0, level: it.level };
          });
          setCbByNode(map);
        } else {
          setCbByNode({});
        }
      } catch {
        setCbByNode({});
      }
    })();
  }, [showNetwork, user, networkData]);

  // === Wallet balance ===
  useEffect(() => {
    if (!user) return;
    (async () => {
      setWalletLoading(true);
      try {
        let entries = [];

        let res = await fetchWithAuth(`/wallet/${user.id}/entries`);
        if (!res.ok) {
          res = await fetchWithAuth(`/wallet/entries?userId=${encodeURIComponent(user.id)}`);
        }
        if (res.ok) entries = await res.json();

        const sumCents = Array.isArray(entries)
          ? entries.reduce((s, e) => s + Math.round(Number(e?.amount || 0) * 100), 0)
          : 0;

        setWalletBalance(sumCents / 100);
      } catch {
        setWalletBalance(0);
      } finally {
        setWalletLoading(false);
      }
    })();
  }, [user]);

  // === Receipts ===
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const res = await fetchWithAuth(`/users/${user.id}/receipts`);
        if (!res.ok) {
          setReceipts([]);
          return;
        }
        const data = await res.json();
        setReceipts(Array.isArray(data) ? data : []);
      } catch {
        setReceipts([]);
      }
    })();
  }, [user]);

  // === Referral bonus ===
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const month = ymNow();

	let res = await fetchWithAuth(
	  `/wallet/${user.id}/entries?source=${encodeURIComponent('REFERRAL_BONUS')}&month=${encodeURIComponent(month)}`
	);
        if (!res.ok) {
          res = await fetchWithAuth(`/wallet/${user.id}/entries`);
        }

        let entries = [];
        if (res.ok) entries = await res.json();

        const normalizeMonth = (row) => {
          if (row?.month) return String(row.month).slice(0, 7);
          if (row?.createdAt) {
            const d = new Date(row.createdAt);
            if (!isNaN(d)) {
              const y = d.getFullYear();
              const m = String(d.getMonth() + 1).padStart(2, '0');
              return `${y}-${m}`;
            }
          }
          return null;
        };

        const filtered = Array.isArray(entries)
          ? entries.filter(e => {
              const isRef = String(e?.source || e?.type || '').toUpperCase() === 'REFERRAL_BONUS';
              const mm = normalizeMonth(e);
              return isRef && mm === month;
            })
          : [];

        const totalCents = filtered.reduce((s, e) => s + Math.round(Number(e?.amount || 0) * 100), 0);
        setRefBonusTotal(totalCents / 100);

        const byChild = {};
        filtered.forEach(e => {
          const childId = parseReferralSourceChildId(e?.source_id || e?.sourceId || e?.child_id);

          if (!childId) return;
          byChild[childId] = (byChild[childId] || 0) + Number(e?.amount || 0);
        });

        setRefBonusByChild(byChild);
      } catch {
        setRefBonusTotal(0);
        setRefBonusByChild({});
      }
    })();
  }, [user]);

  // === Farebná logika siete ===
  useEffect(() => {
    if (!showNetwork || !user) return;

    const ids = new Set([user.id]);
    networkData.forEach(({ parent_id, child_id }) => {
      ids.add(parent_id);
      ids.add(child_id);
    });

(async () => {
  const colorMap = {};

  await Promise.all(
    Array.from(ids).map(async (id) => {
      try {
        const res = await fetchWithAuth(`/referral/network/${id}/status`);

        if (!res.ok) {
          colorMap[id] = 'red';
          return;
        }

        const { aktivny, hasFuel } = await res.json();

        if (!aktivny) {
          colorMap[id] = 'red';
        } else if (hasFuel) {
          colorMap[id] = 'green';
        } else {
          colorMap[id] = 'gray';
        }
      } catch {
        colorMap[id] = 'red';
      }
    })
  );

  setNodeColors(colorMap);
})();

  }, [showNetwork, user, networkData]);

  // === FORM ===
  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const save = async () => {
    const res = await fetchWithAuth('/auth/me', {
      method: 'PUT',

      body: JSON.stringify(form)
    }, i18n.language);
    if (res.ok) {
      const updated = await res.json();
      setUser(updated.user || updated);
      setEditMode(false);
    } else {
      alert(t('SAVE_ERROR'));
    }
  };
  const cancel = () => {
    setForm(user || {});
    setEditMode(false);
  };

  if (!user) return <div>{t('LOADING')}</div>;

  // === Sieť - výpočty ===
  const levelMap = { 1: [], 2: [], 3: [] };
  networkData.forEach(({ generation, child_id }) => {
    if (generation >= 1 && generation <= 3) {
      levelMap[generation].push(child_id);
    }
  });

  const refSumLevel = (lvl) =>
    (levelMap[lvl] || []).reduce((acc, id) => acc + (refBonusByChild[id] || 0), 0);

  const refL1 = refSumLevel(1);
  const refL2 = refSumLevel(2);
  const refL3 = refSumLevel(3);

  const treeMap = {};
  networkData.forEach(({ parent_id, child_id }) => {
    if (!treeMap[parent_id]) treeMap[parent_id] = [];
    treeMap[parent_id].push(child_id);
  });
  const buildTree = id => ({ id, children: (treeMap[id] || []).map(buildTree) });
  const root = buildTree(user.id);

  let nextX = 0;
  const coords = {};
  const dfs = (node, depth) => {
    if (!node.children.length) {
      coords[node.id] = { x: nextX * spacingX + offset, y: depth * levelGap + offset };
      nextX++;
    } else {
      node.children.forEach(child => dfs(child, depth + 1));
      const xs = node.children.map(c => coords[c.id].x);
      coords[node.id] = {
        x: (Math.min(...xs) + Math.max(...xs)) / 2,
        y: depth * levelGap + offset
      };
    }
  };
  dfs(root, 0);

  const allXs = Object.values(coords).map(c => c.x);
  const allYs = Object.values(coords).map(c => c.y);
  const svgWidth = Math.max(...allXs) + offset;
  const svgHeight = Math.max(...allYs) + offset;

  const renderFace = (type) => {
    const Eyes = (
      <>
        <circle cx={-5} cy={-4} r={1.6} fill="#fff" />
        <circle cx={5}  cy={-4} r={1.6} fill="#fff" />
      </>
    );
    if (type === 'smile') {
      return (
        <>
          {Eyes}
          <path d="M -6 4 Q 0 8 6 4" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" />
        </>
      );
    }
    if (type === 'sad') {
      return (
        <>
          {Eyes}
          <path d="M -6 6 Q 0 2 6 6" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" />
        </>
      );
    }
    return (
      <>
        {Eyes}
        <line x1="-5" y1="5" x2="5" y2="5" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
      </>
    );
  };

  const renderNodeContent = ({ isHovered, id, faceType }) => {
    if (isHovered) {
      return (
        <text
          textAnchor="middle"
          dominantBaseline="central"
          fontSize="12"
          fill="#fff"
          fontWeight="bold"
          style={{ pointerEvents: 'none' }}
        >
          {id}
        </text>
      );
    }
    return renderFace(faceType);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', padding: 20 }}>
      <div style={{ flex: '0 0 400px' }}>
        <h2>{t('USER_PROFILE')}</h2>

        <div style={fieldStyle}>
          <label style={labelStyle}>{t('REFERRAL_CODE')}</label>
          <div style={{ position: 'relative', width: 200 }}>
            <input
              type="text"
              value={form.referral_code || ''}
              disabled
              style={{ ...inputStyle, paddingRight: 60, fontWeight: 'bold' }}
            />
            <button
              onClick={() => setShowQR(true)}
              style={{
                position: 'absolute',
                right: 0,
                top: 0,
                height: '100%',
                border: 'none',
                background: '#ddd',
                padding: '0 8px',
                cursor: 'pointer'
              }}
            >
              {t('QR_CODE')}
            </button>
          </div>
        </div>

        {['meno','priezvisko','email','telefon','ulica','mesto','psc','krajina','iban','swift','banka']
          .map(key => (
            <div key={key} style={fieldStyle}>
              <label style={labelStyle}>{t(key.toUpperCase())}</label>
              <input
                type="text"
                value={form[key] || ''}
                onChange={e => updateForm(key, e.target.value)}
                disabled={!editMode || key === 'email'}
                style={inputStyle}
              />
            </div>
        ))}

        <div style={{ marginTop: 20 }}>
          {!editMode ? (
            <>
              <button onClick={() => setEditMode(true)} style={{ marginRight: 10 }}>
                {t('EDIT_PROFILE')}
              </button>
              <button onClick={() => (window.location.href = '/home')}>{t('BACK')}</button>
            </>
          ) : (
            <>
              <button onClick={save} style={{ marginRight: 10 }}>{t('SAVE')}</button>
              <button onClick={cancel}>{t('CANCEL')}</button>
            </>
          )}
        </div>
      </div>

      {showNetwork && (
        <div style={{ background: '#fff', boxShadow: '0 0 10px rgba(0,0,0,0.1)', borderRadius: 8, padding: 20, overflow: 'auto', marginTop: '70px', marginLeft: '-15px' }}>
          <h4>{t('YOUR_NETWORK')}</h4>
          <div>{t('LEVEL1', { level: 1 })}: <strong>{levelMap[1].length}</strong></div>
          <div>{t('LEVEL2', { level: 2 })}: <strong>{levelMap[2].length}</strong></div>
          <div>{t('LEVEL3', { level: 3 })}: <strong>{levelMap[3].length}</strong></div>
          <div>Celkový počet členov v sieti: <strong>{networkData.length}</strong></div>

          <div style={{ margin: '12px 0' }}>
            {[
              { color: 'red',   key: 'LEGEND.RED' },
              { color: 'gray',  key: 'LEGEND.GRAY' },
              { color: 'green', key: 'LEGEND.GREEN' }
            ].map(({ color, key }) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <svg width={radius * 2} height={radius * 2} style={{ marginRight: 8 }}>
                  <g transform={`translate(${radius},${radius})`}>
                    <circle r={radius} fill={color} stroke="#fff" strokeWidth={2} />
                    {renderFace(faceByColor[color] || 'awkward')}
                  </g>
                </svg>
                <span>{t(key)}</span>
              </div>
            ))}
          </div>

          <svg width={svgWidth} height={svgHeight} style={{ background: '#fafafa', border: '1px solid #ddd', borderRadius: 8 }}>
            {networkData.map((l, i) => {
              const p = coords[l.parent_id];
              const c = coords[l.child_id];
              if (!p || !c) return null;
              return (
                <line key={i} x1={p.x} y1={p.y} x2={c.x} y2={c.y} stroke="#107c6b" strokeWidth={2} />
              );
            })}
            {Object.entries(coords).map(([idStr, { x, y }]) => {
              const id = Number(idStr);
              const color = nodeColors[id] || 'red';
              const faceType = faceByColor[color] || 'awkward';
              const isSelected = id === selectedNodeId;
              const isHovered = id === hoveredNodeId;

              const hasReferralBonus = (refBonusByChild[id] || 0) > 0;
             
              return (
                <g
                  key={id}
                  transform={`translate(${x},${y})`}
                  style={{ cursor: 'default' }}
                  onMouseEnter={(e) => {
                    setHoveredNodeId(id);
                    setHoveredNode({ x: e.clientX, y: e.clientY, color, id });
                  }}
                  onMouseLeave={() => {
                    setHoveredNodeId(null);
                    setHoveredNode(null);
                  }}
                >
                  <circle
                    r={radius}
                    fill={color}
                    stroke={isSelected || isHovered ? '#000' : '#fff'}
                    strokeWidth={isSelected || isHovered ? 3 : 2}
                  />
                  {renderNodeContent({ isHovered, id, faceType })}

                  {hasReferralBonus && (
                    <text
                      x={radius + 1}
                      y={-radius + 1}
                      fontSize="14"
                      fontWeight="bold"
                      fill="#000"
                      textAnchor="start"
                      dominantBaseline="hanging"
                      style={{ pointerEvents: 'none' }}
                    >
                      B
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        </div>
      )}

      {/* PANEL: Wallet + Cashback + Bločky */}
      {showNetwork && (
        <div
          style={{
            minWidth: 420,
            background: '#fff',
            boxShadow: '0 0 10px rgba(0,0,0,0.1)',
            borderRadius: 8,
            padding: 20,
            marginTop: '70px',
            marginLeft: 12
          }}
        >
          <div style={{ marginBottom: 12 }}>
            <strong>Wallet:</strong>{' '}
            {walletLoading ? '—' : formatCurrency(walletBalance)}
          </div>

          <h4>Cashback {ymNow()}</h4>
          <div style={{ margin: '6px 0' }}>
            <span>Osobný cashback:</span>{' '}
            <strong>{formatCurrency(cbBase)}</strong>
          </div>
          <div style={{ margin: '6px 0' }}>
            <span>L1 cashback:</span>{' '}
            <strong>{formatCurrency(cbL1)}</strong>
          </div>
          <div style={{ margin: '6px 0' }}>
            <span>L2 cashback:</span>{' '}
            <strong>{formatCurrency(cbL2)}</strong>
          </div>
          <div style={{ margin: '6px 0' }}>
            <span>L3 cashback:</span>{' '}
            <strong>{formatCurrency(cbL3)}</strong>
          </div>
          <div style={{ marginTop: 6 }}>
            <span>Celkom:</span>{' '}
            <strong>{formatCurrency(cbTotal)}</strong>
          </div>

          <div style={{ marginTop: 10 }}>
            <div style={{ fontWeight: 'bold' }}>Referral bonus {ymNow()}</div>
            <div style={{ margin: '6px 0' }}>
              <span>Bonus celkom:</span>{' '}
              <strong>{formatCurrency(refBonusTotal)}</strong>
            </div>
            <div style={{ margin: '6px 0' }}>
              <span>L1:</span>{' '}
              <strong>{formatCurrency(refL1)}</strong>
            </div>
            <div style={{ margin: '6px 0' }}>
              <span>L2:</span>{' '}
              <strong>{formatCurrency(refL2)}</strong>
            </div>
            <div style={{ margin: '6px 0' }}>
              <span>L3:</span>{' '}
              <strong>{formatCurrency(refL3)}</strong>
            </div>
          </div>

          <div style={{ height: 1, background: '#eee', margin: '12px 0' }} />

          <h4>Bločky</h4>
          {receipts.length === 0 ? (
            <div style={{ fontStyle: 'italic', opacity: 0.7 }}>-</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ display: 'flex', marginBottom: 6, fontWeight: 'bold', fontSize: 12 }}>
                <div style={{ flex: 0.3 }}>ID</div>
                <div style={{ flex: 0.9 }}>Pumpa</div>
                <div style={{ flex: 1.1 }}>Množstvo</div>
                <div style={{ flex: 0.8 }}>Suma</div>
                <div style={{ flex: 2 }}>Dátum</div>
              </div>
              {receipts.map((r, i) => {
                const qty = `${Number(r.units ?? r.liters ?? 0)} ${String(r.fuel_type || '').toLowerCase() === 'electric' ? 'kWh' : 'l'}`;
                const amount = formatCurrency(Number(r.total_price ?? r.amount ?? 0));
                const date = r.createdAt ? new Date(r.createdAt) : (r.date ? new Date(r.date) : null);
                const dateStr = date && !isNaN(date.getTime()) ? date.toLocaleString() : '-';
                return (
                  <div key={r.id ?? i} style={{ display: 'flex', marginBottom: 4, fontSize: 12 }}>
                    <div style={{ flex: 0.3 }}>{r.id}</div>
                    <div style={{ flex: 0.9 }}>{r.store_name || 'Neznáma pumpa'}</div>
                    <div style={{ flex: 1.1 }}>{qty}</div>
                    <div style={{ flex: 0.8 }}>{amount}</div>
                    <div style={{ flex: 2 }}>{dateStr}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {showQR && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={() => setShowQR(false)}
        >
          <div
            style={{ background: '#fff', padding: 20, borderRadius: 8, textAlign: 'center', minWidth: 280 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>{t('QR_CODE')}</h3>
            <QRCodeCanvas value={form.referral_code || ''} size={220} />
            <div style={{ marginTop: 20 }}>
              <button onClick={() => setShowQR(false)}>{t('CLOSE')}</button>
            </div>
          </div>
        </div>
      )}

      {hoveredNode && (
        <div
          style={{
            position: 'fixed',
            top: hoveredNode.y + 10,
            left: hoveredNode.x + 10,
            background: '#fff',
            padding: '6px 10px',
            border: '1px solid #ccc',
            borderRadius: 4,
            whiteSpace: 'pre-line',
            pointerEvents: 'none',
            fontSize: 13
          }}
        >
          {(() => {
            const color = hoveredNode.color;
            if (color !== 'green') {
              return color === 'red'
                ? 'Bez platnej aktivácie\nNeráta sa do cashbacku'
                : 'Plne registrovaný bez aktivity\nNeráta sa do cashbacku';
            }
            const id = hoveredNode.id;

            if (id === user.id) {
              return (
                <>
                  <div>Bude vyplatený cashback: <strong>{formatCurrency(cbBase)}</strong></div>
                  {refBonusTotal > 0 && (
                    <div>Bude vyplatená referral odmena: <strong>{formatCurrency(refBonusTotal)}</strong></div>
                  )}
                </>
              );
            }

            const amount = cbByNode[id]?.amount || 0;
            const refForThisChild = refBonusByChild[id] || 0;
            return (
              <>
                <div>Bude vyplatený cashback: <strong>{formatCurrency(amount)}</strong></div>
                {refForThisChild > 0 && (
                  <div>Referral odmena za tohto člena: <strong>{formatCurrency(refForThisChild)}</strong></div>
                )}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default UserProfile;
